import { buildApiUrl } from "../../services/apiConfig.js";

class ChatbotService {
  constructor() {
    this.NOVA_API = buildApiUrl('NOVA', 'Nova_chat');
    this.CUST_BUILD_API = buildApiUrl('EXPORT_USE_CASE', '/extract-use-case');
    this.CONFIRM_USE_CASE_API = buildApiUrl('EXPORT_USE_CASE', '/confirm-use-case');
    this.UPLOAD_API = buildApiUrl('UPLOAD', '/upload-documents');
    this.ANALYZE_DOCUMENTS_API = buildApiUrl('EXPORT_USE_CASE', '/analyze-documents');
    this.CONVERSATION_API = buildApiUrl('EXPORT_USE_CASE', '/conversation');
    this.CONVERSATION_UPDATE_API = buildApiUrl('EXPORT_USE_CASE', '/conversation-update');
    this.USE_CASE_INFO_API = buildApiUrl('EXPORT_USE_CASE', '/use-case-info');
  }

  async sendTextMessage(input, userId, options = {}) {
    // If we have a conversation ID, use the conversation update endpoint
    if (options.conversation_id) {
      return this.updateConversation(input, userId, options.conversation_id, options.collection_name);
    }

    const body = {
      prompt: input,
      user_id: userId,
      ...options  // This allows passing collection_name and conversation_history
    };

    // Use the conversation endpoint if we have conversation history
    if (options.conversation_history && options.conversation_history.length > 0) {
      return this.sendConversationMessage(input, userId, options);
    }

    const response = await fetch(this.CUST_BUILD_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    return response.json();
  }

  async sendConversationMessage(input, userId, options = {}) {
    const body = {
      prompt: input,
      user_id: userId,
      conversation_history: options.conversation_history || [],
      collection_name: options.collection_name,
      use_case_id: options.use_case_id
    };

    const response = await fetch(this.CONVERSATION_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const data = await response.json();

    // If we have a use case suggestion, format it like the extract-use-case response
    if (data.use_case_suggestion) {
      return {
        ...data.use_case_suggestion,
        response: data.response,
        needs_confirmation: true
      };
    }

    return {
      response: data.response,
      suggested_actions: data.suggested_actions
    };
  }

  async updateConversation(input, userId, conversationId, collectionName = null) {
    const body = {
      prompt: input,
      user_id: userId,
      conversation_id: conversationId,
      collection_name: collectionName
    };

    console.log(`Sending to conversation-update endpoint:`, body);

    try {
      const response = await fetch(this.CONVERSATION_UPDATE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Conversation update error:", errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log("Conversation update response:", data);

      return data;
    } catch (error) {
      console.error("Error in updateConversation:", error);
      throw error;
    }
  }

  async uploadFiles(files, collectionName) {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('collection_name', collectionName);

    try {
      const response = await fetch(this.UPLOAD_API, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload API error:", errorText);

        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(`Upload failed: ${response.status} ${errorJson.detail || errorText}`);
        } catch (e) {
          throw new Error(`Upload failed: ${response.status} ${errorText}`);
        }
      }

      return response.json();
    } catch (error) {
      console.error("File upload error:", error);

      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new Error("Network error: Unable to connect to the upload service. Please check your connection and try again.");
      }

      throw error;
    }
  }

  async analyzeDocuments(files, collectionName, prompt = null) {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('collection_name', collectionName);

    if (prompt) {
      formData.append('prompt', prompt);
    }

    try {
      const response = await fetch(this.ANALYZE_DOCUMENTS_API, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Document analysis API error:", errorText);
        throw new Error(`Document analysis failed: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Document analysis error:", error);
      throw error;
    }
  }

  async confirmUseCase(confirmationData) {
    const response = await fetch(this.CONFIRM_USE_CASE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(confirmationData),
    });

    if (!response.ok) {
      throw new Error(`Failed to confirm use case: ${response.status}`);
    }

    return response.json();
  }

  async getUseCaseInfo(useCaseId, userId) {
    const response = await fetch(this.USE_CASE_INFO_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        use_case_id: useCaseId,
        user_id: userId
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get use case info: ${response.status}`);
    }

    return response.json();
  }

  async saveUseCase(useCase) {
    const response = await fetch(buildApiUrl('AUTH', '/table-backend/use_cases'), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(useCase)
    });

    if (!response.ok) {
      throw new Error(`Failed to save use case: ${response.status}`);
    }

    return response.json();
  }

  async saveAgent(agent) {
    const response = await fetch(buildApiUrl('AUTH', '/table-backend/widgets'), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(agent)
    });

    if (!response.ok) {
      throw new Error(`Failed to save agent: ${response.status}`);
    }

    return response.json();
  }

  getOrGenerateThreadID() {
    let threadID = sessionStorage.getItem("thread_id");
    if (!threadID) {
      threadID = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("thread_id", threadID);
    }
    return threadID;
  }
}

export default new ChatbotService();